import { useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { Spinner } from "@/components/ui/Spinner";
import {
  useApplication,
  useApplicationMutations,
  useApplicationsOverview,
  useDecisions,
  useDocTemplates,
  useSelectableOffices,
  useParcels,
} from "../../hooks";
import type { OfficialApplicationDetail } from "../../types";
import { InfoHint } from "./InfoHint";
import { isProjectParcel } from "../../utils";

/**
 * Edycja wniosku PRZED złożeniem — dostępna dla autora, dyrektora i CEO
 * (backendowe `can_edit`): adresat, gmina/powiat, decyzja, zakres działek, uwagi.
 * Zapis zmiany zakresu przechodzi przez te same reguły unikalności co założenie
 * wniosku, a przy zatwierdzonej fazie „Zakres i adresat" backend cofa ją do
 * edycji (i unieważnia kompilację) — jak ręczne „Cofnij do edycji".
 */
export function EditApplicationModal({
  applicationId,
  onClose,
}: {
  applicationId: string;
  onClose: () => void;
}) {
  const { data: app } = useApplication(applicationId);
  return (
    <Modal open onClose={onClose} title={app ? `Edycja wniosku ${app.number}` : "Edycja wniosku"} size="lg">
      {!app ? (
        <div className="py-8 flex justify-center"><Spinner /></div>
      ) : (
        <EditForm app={app} onClose={onClose} />
      )}
    </Modal>
  );
}

function EditForm({ app, onClose }: { app: OfficialApplicationDetail; onClose: () => void }) {
  const m = useApplicationMutations(app.id);
  const { data: overview } = useApplicationsOverview(app.connection);
  const { data: offices } = useSelectableOffices();
  const { data: decisions } = useDecisions({ connection: app.connection });
  const { data: parcels } = useParcels(app.connection);
  // Wniosek uniwersalny: szablon JEST typem pisma, więc póki wniosek jest edytowalny,
  // da się go tu podmienić (np. urząd chce inny druk niż zakładaliśmy).
  const { data: templates } = useDocTemplates();

  const config = app.config;
  const recipientMode = config?.recipient_mode ?? "";
  // Wnioski zakotwiczone na WPW (pakiet WPW / art. 124): działka + właściciel są
  // tożsamością wniosku — zakresu się nie zmienia, do edycji zostają uwagi.
  const wpwAnchored = config?.unique_rule === "per_wpw" || recipientMode === "negotiator";
  const hasOfficeField =
    ["office_owner", "starostwo", "gmina", "any_office"].includes(recipientMode) && !wpwAnchored;
  const hasParcelsField = !wpwAnchored;

  const [office, setOffice] = useState(app.recipient_office ?? "");
  const [docTemplate, setDocTemplate] = useState(app.doc_template ?? "");
  const [decision, setDecision] = useState(app.decision ?? "");
  const [commune, setCommune] = useState(app.commune);
  const [county, setCounty] = useState(app.county);
  const [notes, setNotes] = useState(app.notes);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(app.parcels_brief.map((p) => p.id)),
  );
  const [error, setError] = useState("");

  const officeOptions = useMemo(() => {
    if (recipientMode === "office_owner") {
      return (overview?.office_owners ?? []).map((o) => ({
        value: o.office_id,
        label: `${o.office_name} (${o.parcel_count} dz.)${
          o.existing_application && o.existing_application.id !== app.id ? " — MA JUŻ WNIOSEK" : ""
        }`,
      }));
    }
    // any_office (wniosek uniwersalny) — cały rejestr, bez zawężania rodzajem.
    const kinds = recipientMode === "starostwo" ? ["starostwo"] : undefined;
    // Jak w kreatorze: starostwa właściwe dla powiatów projektu na górze i z ★.
    const projectCounties = (overview?.counties ?? [])
      .map((c) => c.replace(/^powiat\s+/i, "").trim().toLowerCase())
      .filter(Boolean);
    const matches = (name: string) =>
      projectCounties.some((c) => c && name.toLowerCase().includes(c));
    const rows = (offices ?? [])
      .filter((o) => !kinds || kinds.includes(o.kind))
      .map((o) => ({
        value: o.id,
        label: kinds && matches(o.name) ? `★ ${o.name} — powiat projektu` : o.name,
        _match: kinds ? matches(o.name) : false,
      }));
    return rows
      .sort((a, b) => Number(b._match) - Number(a._match) || a.label.localeCompare(b.label, "pl"))
      .map(({ value, label }) => ({ value, label }));
  }, [recipientMode, overview, offices, app.id]);

  // Decyzja już ostateczna nie ma o co wnioskować — zostaje tylko ta już wybrana
  // (żeby edycja innego pola nie gubiła wyboru) i nieostateczne.
  const decisionOptions = useMemo(
    () => (decisions ?? [])
      .filter((d) => d.finality !== "final" || d.id === app.decision)
      .map((d) => ({
        value: d.id, label: `${d.sign} — ${d.kind_display} (${d.finality_display})`,
      })),
    [decisions, app.decision],
  );

  const toggleParcel = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const scopeApproved = app.phases.find((p) => p.key === "scope")?.state === "approved";

  const save = () => {
    setError("");
    const payload: Record<string, unknown> = {};
    if (hasOfficeField && office && office !== (app.recipient_office ?? "")) {
      payload.recipient_office = office;
    }
    if (config?.template_pick && docTemplate && docTemplate !== (app.doc_template ?? "")) {
      payload.doc_template = docTemplate;
    }
    if (recipientMode === "decision_organ" && decision && decision !== (app.decision ?? "")) {
      payload.decision = decision;
    }
    if (recipientMode === "gmina" && commune !== app.commune) payload.commune = commune;
    if (app.application_type === "odrolnienie" && county !== app.county) payload.county = county;
    if (hasParcelsField) {
      const current = new Set(app.parcels_brief.map((p) => p.id));
      const changed = selected.size !== current.size || [...selected].some((id) => !current.has(id));
      if (changed) payload.parcel_ids = [...selected];
    }
    if (notes !== app.notes) payload.notes = notes;
    if (!Object.keys(payload).length) { onClose(); return; }
    m.update.mutate(
      { id: app.id, payload },
      {
        onSuccess: onClose,
        onError: (e: unknown) => {
          const detail = (e as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setError(typeof detail === "string" ? detail : "Nie udało się zapisać zmian.");
        },
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="text-xs text-[var(--color-text-secondary)]">
        {app.type_label} · {app.project_name}
      </div>

      {scopeApproved && (
        <div className="text-xs border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2">
          Faza „Zakres i adresat” jest zatwierdzona — zapisanie zmian zakresu cofnie jej
          zatwierdzenie i unieważni skompilowany pakiet.
        </div>
      )}

      {wpwAnchored && (
        <div className="text-xs text-[var(--color-text-secondary)]">
          Wniosek jest zakotwiczony na działce{app.wpw_parcel_teryt ? (
            <> <span className="font-mono">{app.wpw_parcel_teryt}</span></>
          ) : null} — zakres i adresat wynikają z WPW i nie podlegają edycji; możesz
          zmienić uwagi.
        </div>
      )}

      {config?.template_pick && (
        <label className="block">
          <span className="section-label flex items-center gap-1">
            Szablon pisma
            <InfoHint text="Wniosek uniwersalny nie ma własnego druku — czym jest to pismo, mówi wskazany szablon rodzaju „Uniwersalny” (jego nazwa stoi przy wniosku na liście). Podmiana nie rusza już wygenerowanych pism: nowy druk powstaje dopiero przy kolejnej generacji." />
          </span>
          <SearchableSelect
            options={(templates ?? [])
              .filter((t) => t.kind === config.doc_template_kind)
              .map((t) => ({ value: t.id, label: t.name }))}
            value={docTemplate}
            placeholder="Wybierz szablon…"
            onChange={(e) => setDocTemplate(e.target.value)}
          />
        </label>
      )}

      {hasOfficeField && (
        <label className="block">
          <span className="section-label flex items-center gap-1">
            Adresat (urząd)
            <InfoHint text={
              recipientMode === "office_owner"
                ? "Lista pokazuje urzędy będące właścicielami działek w projekcie. Reguła: 1 urząd = 1 wniosek na projekcie — duplikat zostanie odrzucony przy zapisie."
                : recipientMode === "starostwo"
                  ? "Starostwo/prezydent miasta właściwy dla działek. Właściwe dla powiatów projektu są oznaczone ★."
                  : recipientMode === "any_office"
                    ? "Cały rejestr urzędów — wniosek uniwersalny bywa adresowany gdziekolwiek (konserwator, wojewoda, zarząd dróg)."
                    : "Urząd gminy, do którego trafi wniosek ULICP."
            } />
          </span>
          <SearchableSelect
            options={officeOptions}
            value={office}
            placeholder="Wybierz urząd…"
            onChange={(e) => setOffice(e.target.value)}
          />
        </label>
      )}

      {recipientMode === "gmina" && (
        <label className="block">
          <span className="section-label flex items-center gap-1">
            Gmina
            <InfoHint text="Reguła: 1 gmina = 1 wniosek ULICP na projekcie. Podpowiedzi z gmin działek projektu." />
          </span>
          <SearchableSelect
            options={(overview?.communes ?? []).map((g) => ({ value: g, label: g }))}
            value={commune}
            placeholder="Wybierz gminę…"
            onChange={(e) => setCommune(e.target.value)}
          />
        </label>
      )}

      {app.application_type === "odrolnienie" && (
        <label className="block">
          <span className="section-label flex items-center gap-1">
            Powiat
            <InfoHint text="Powiat, którego starosta wyda zezwolenie. Podpowiedzi z powiatów działek projektu." />
          </span>
          <SearchableSelect
            options={(overview?.counties ?? []).map((p) => ({ value: p, label: p }))}
            value={county}
            placeholder="Wybierz powiat…"
            onChange={(e) => setCounty(e.target.value)}
          />
        </label>
      )}

      {recipientMode === "decision_organ" && (
        <label className="block">
          <span className="section-label flex items-center gap-1">
            Decyzja z rejestru
            <InfoHint text="Decyzja, którą klauzulujesz — adresatem jest organ wydający. Reguła: 1 decyzja = 1 aktywny wniosek o klauzulę." />
          </span>
          <SearchableSelect
            options={decisionOptions}
            value={decision}
            placeholder="Wybierz decyzję…"
            onChange={(e) => setDecision(e.target.value)}
          />
        </label>
      )}

      {hasParcelsField && (
        <div>
          <span className="section-label flex items-center gap-1">
            Działki w zakresie ({selected.size})
            <InfoHint text="Zakres wniosku — działki wejdą do map i pisma. Dla odrolnienia zmiana zakresu przelicza kwalifikację działek." />
          </span>
          {!parcels ? (
            <Spinner size="sm" />
          ) : (
            <div className="mt-1 max-h-56 overflow-auto border border-[var(--color-border)]">
              {/* Działki poza bieżącą trasą (pozostałe po starszej wersji trasy) nie są
                  częścią projektu — pokazujemy je tylko wtedy, gdy już są w zakresie
                  wniosku, żeby dało się je z niego wypiąć. Ręcznie wykorzystywane są
                  w grze jak działki z trasy (utils.isProjectParcel). */}
              {parcels.filter((p) => isProjectParcel(p) || selected.has(p.id)).map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-2 py-1 text-xs border-b border-[var(--color-border)] last:border-b-0 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selected.has(p.id)}
                    onChange={() => toggleParcel(p.id)}
                  />
                  <span className="font-mono">{p.teryt}</span>
                  {p.obreb && (
                    <span className="text-[10px] text-[var(--color-text-muted)]">obręb {p.obreb}</span>
                  )}
                </label>
              ))}
            </div>
          )}
        </div>
      )}

      <label className="block">
        <span className="section-label">Uwagi</span>
        <textarea
          className="input-base w-full text-xs"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </label>

      {error && <div className="text-xs text-red-600">{error}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>Anuluj</Button>
        <Button onClick={save} loading={m.update.isPending}>Zapisz zmiany</Button>
      </div>
    </div>
  );
}
